import { cn } from '@h2hspark/ui';
import { Check } from 'lucide-react';
import * as React from 'react';

import {
  CHECKOUT_STEPS,
  CHECKOUT_STEPS_LABEL,
  CHECKOUT_STEP_CURRENT_SUFFIX,
  CHECKOUT_STEP_DONE_SUFFIX,
  type CheckoutStepId,
} from './constants';

/**
 * **The reservation flow's stepper** (`Web App.dc.html` §3.1, lines 1002–1004).
 *
 * ## Transcribed
 *
 * A row of `display:flex; gap:10px` above the step heading: per step a dot carrying either its
 * number or a `check` once it is behind you, its label beside it, and a `28px × 2px`
 * `--border-default` bar between one step and the next. The current step's dot is the brand fill
 * with white on it; a step still ahead is a hairline circle in muted ink.
 *
 * Which steps, and why two rather than the prototype's three, is `CHECKOUT_STEPS`' own docblock.
 *
 * ## It is a list, and it says which step you are on in words
 *
 * `NFR accessibility#wcag-aa`. A `<ol>` of `<li>`s, so a reader is told how many steps there are
 * and which one this is; the state is in the accessible name — *"Reserve, current step"* — and not
 * only in a fill colour and a tick, because both of those are invisible to a reader and one of
 * them is invisible to somebody who cannot separate purple from grey. `aria-current="step"` names
 * the position for a reader that navigates by it.
 *
 * The dots and the bars are `aria-hidden`: they draw the same fact the labels already carry, and a
 * reader announcing "1 2 3" between the words is noise.
 */
export function CheckoutSteps({
  current,
}: {
  readonly current: CheckoutStepId;
}): React.ReactElement {
  const currentIndex = CHECKOUT_STEPS.findIndex((step) => step.id === current);

  return (
    <ol aria-label={CHECKOUT_STEPS_LABEL} className="flex flex-wrap items-center gap-2.5">
      {CHECKOUT_STEPS.map((step, index) => {
        const done = index < currentIndex;
        const here = index === currentIndex;
        return (
          <li
            key={step.id}
            className="flex items-center gap-2.5"
            /* On the `<li>` rather than on an inner span: `aria-current` names *this item in this
               list*, and a reader navigating the list by item is the reader it is for. */
            {...(here ? { 'aria-current': 'step' as const } : {})}
          >
            <span className="flex items-center gap-2.5">
              <span
                aria-hidden
                className={cn(
                  'flex h-6 w-6 flex-none items-center justify-center rounded-pill text-xs font-bold',
                  done && 'bg-brand text-white',
                  here && 'bg-brand text-white',
                  !done && !here && 'border border-border text-text-subtle',
                )}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : index + 1}
              </span>
              <span
                className={cn(
                  'font-brand text-sm font-bold',
                  here ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {step.label}
                {/* The state, in words, for a reader. Off-screen rather than absent: it is the
                    same fact the fill and the tick draw, and those two are the only ones a
                    sighted reader gets. */}
                <span className="sr-only">
                  {done ? ` ${CHECKOUT_STEP_DONE_SUFFIX}` : null}
                  {here ? ` ${CHECKOUT_STEP_CURRENT_SUFFIX}` : null}
                </span>
              </span>
            </span>
            {index < CHECKOUT_STEPS.length - 1 ? (
              <span aria-hidden className="h-0.5 w-7 flex-none bg-border" />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
